import { For, Show, createMemo, createSignal, createEffect, onMount, onCleanup } from "solid-js";
import type { Component } from "solid-js";
import { A, useLocation } from "@solidjs/router";
import { useAuth } from "@auth/session";
import { useShellStore } from "@ui/state/shell";
import type { NavSection } from "@ui/state/shell";
import type { DashboardStats } from "@modules/stats/service";
import {
  useDashboardStatsQuery,
  initializeDashboardStatsSubscription,
  cleanupDashboardStatsSubscription,
  dashboardStats
} from "@modules/stats/service";

type NavItem = {
  id: NavSection;
  label: string;
  iconClass: string;
  badge?: string;
};

type UtilityItem = {
  id: "notifications" | "account";
  label: string;
  iconClass: string;
  badge?: string;
};

type MaybeAccessor<T> = T | (() => T) | undefined;

const read = <T,>(input: MaybeAccessor<T>): T | undefined =>
  typeof input === "function" ? (input as () => T)() : input;

const primaryNav: NavItem[] = [
  { id: "dashboard", label: "Dashboard", iconClass: "fa-solid fa-chart-line" },
  { id: "bookings", label: "Bookings", iconClass: "fa-solid fa-calendar-check" },
  // { id: "payments", label: "Payments", iconClass: "fa-solid fa-credit-card" },  // temporarily hidden
  { id: "locations", label: "Locations", iconClass: "fa-solid fa-location-dot" },
  { id: "robots", label: "Robots", iconClass: "fa-solid fa-robot" },
  { id: "users", label: "Users", iconClass: "fa-solid fa-user-group" },
];

const supportNav: NavItem[] = [
  { id: "chats", label: "HappyChat", iconClass: "fa-solid fa-comments" },
  { id: "settings", label: "Settings", iconClass: "fa-solid fa-sliders" },
];

const utilities: UtilityItem[] = [
  { id: "notifications", label: "Notifications", iconClass: "fa-solid fa-bell" },
  { id: "account", label: "Account", iconClass: "fa-solid fa-circle-user" },
];

const NavLink: Component<{
  href: string;
  label: string;
  badge?: string;
  iconClass: string;
  end?: boolean;
  onClick?: () => void;
}> = (props) => (
  <div class="nav-tooltip-wrapper">
    <A
      href={props.href}
      class="nav-icon-button"
      activeClass="is-active"
      aria-label={props.label}
      end={props.end}
      onClick={props.onClick}
    >
      <Show when={props.badge} fallback={<i class={`nav-icon-symbol ${props.iconClass}`}></i>}>
        <span class="icon-with-badge">
          <i class={`nav-icon-symbol ${props.iconClass}`}></i>
          <span class="icon-badge">{props.badge}</span>
        </span>
      </Show>
    </A>
    <div class="nav-tooltip">{props.label}</div>
  </div>
);

const AccountButton: Component = () => {
  const { signOut } = useAuth();
  const [isDropdownOpen, setIsDropdownOpen] = createSignal(false);

  const handleToggle = () => setIsDropdownOpen((open) => !open);
  const handleClose = () => setIsDropdownOpen(false);
  const handleSignOut = async () => {
    handleClose();
    await signOut();
  };

  return (
    <div
      class="account-dropdown-wrapper"
      onFocusOut={(event) => {
        const next = event.relatedTarget as Node | null;
        if (!event.currentTarget.contains(next)) {
          handleClose();
        }
      }}
    >
      <button
        type="button"
        class="nav-icon-button"
        aria-label="Account"
        onClick={handleToggle}
      >
        <i class="nav-icon-symbol fa-solid fa-circle-user"></i>
      </button>
      <Show when={isDropdownOpen()}>
        <div class="account-dropdown-menu">
          <button class="dropdown-item" type="button" onClick={handleClose}>
            <i class="fa-solid fa-user"></i>
            <span>Account</span>
          </button>
          <button class="dropdown-item" type="button" onClick={handleSignOut}>
            <i class="fa-solid fa-arrow-right-from-bracket"></i>
            <span>Logout</span>
          </button>
        </div>
      </Show>
    </div>
  );
};

export const Sidebar: Component<{ isOpen: boolean; onNavigate: () => void }> = (props) => {
  const location = useLocation();
  const { actions } = useShellStore();

  // Sync route to ShellStore activeNav for backward compatibility
  createEffect(() => {
    const path = location.pathname;
    let section: NavSection = "dashboard";

    if (path.startsWith("/chats")) section = "chats";
    else if (path.startsWith("/bookings")) section = "bookings";
    else if (path.startsWith("/payments")) section = "payments";
    else if (path.startsWith("/locations")) section = "locations";
    else if (path.startsWith("/robots")) section = "robots";
    else if (path.startsWith("/users")) section = "users";
    else if (path.startsWith("/notifications")) section = "notifications";
    else if (path.startsWith("/settings")) section = "settings";

    actions.setActiveNav(section);
  });

  const dashboardStatsQuery = useDashboardStatsQuery();

  onMount(() => {
    initializeDashboardStatsSubscription();
  });

  onCleanup(() => {
    cleanupDashboardStatsSubscription();
  });
  const stats = createMemo(() => {
    const live = dashboardStats();
    if (live) {
      return live;
    }

    const value = read(dashboardStatsQuery.data) as DashboardStats | undefined;
    const fallbackNotifications = value?.notifications ?? { total: 0, bookings: 0, robots: 0, users: 0, chats: 0 };

    return {
      notifications: {
        total: fallbackNotifications.total ?? 0,
        bookings: fallbackNotifications.bookings ?? 0,
        robots: fallbackNotifications.robots ?? 0,
        users: fallbackNotifications.users ?? 0,
        chats: fallbackNotifications.chats ?? 0,
      },
    };
  });

  const notificationsCounters = () => stats().notifications;
  const chatsBadge = () => {
    const count = notificationsCounters().chats;
    return count > 0 ? Math.min(count, 99).toString() : undefined;
  };
  const bookingsBadge = () => {
    const count = notificationsCounters().bookings;
    return count > 0 ? Math.min(count, 99).toString() : undefined;
  };
  // Removed: paymentsAwaitingSchedule badge
  // Now using user-specific notification counters instead of global stats
  const notificationsBadge = () => {
    const total = notificationsCounters().total;
    return total > 0 ? Math.min(total, 99).toString() : undefined;
  };

  return (
    <aside class="app-sidebar" classList={{ "mobile-collapsed": !props.isOpen }}>
      <div class="sidebar-logo">
        <img src="/logo.png" alt="HappyTidy" />
        <span>BackOffice</span>
      </div>

      <div class="sidebar-section" aria-label="Main navigation">
        <For each={primaryNav}>
          {(item) => (
            <NavLink
              href={item.id === "dashboard" ? "/" : `/${item.id}`}
              iconClass={item.iconClass}
              label={item.label}
              end={item.id === "dashboard"}
              badge={item.id === "bookings" ? bookingsBadge() : undefined}
              onClick={props.onNavigate}
            />
          )}
        </For>
      </div>

      <div class="sidebar-section" aria-label="Support navigation">
        <For each={supportNav}>
          {(item) => (
            <NavLink
              href={`/${item.id}`}
              iconClass={item.iconClass}
              label={item.label}
              badge={item.id === "chats" ? chatsBadge() : item.badge}
              onClick={props.onNavigate}
            />
          )}
        </For>
      </div>

      <div class="sidebar-section sidebar-section--utilities" aria-label="Quick actions">
        <For each={utilities}>
          {(item) => (
            <Show when={item.id !== "account"} fallback={<AccountButton />}>
              <NavLink
                href={`/${item.id}`}
                iconClass={item.iconClass}
                label={item.label}
                badge={item.id === "notifications" ? notificationsBadge() : item.badge}
                onClick={props.onNavigate}
              />
            </Show>
          )}
        </For>
      </div>
    </aside>
  );
};

